import { useState } from 'react';
import { Camera, Check, Sparkles, X } from 'lucide-react';
import { useQueue } from '../../context/QueueContext';
import { normalizeImageForAI } from '../../utils/siteConfig';

const API = process.env.REACT_APP_BACKEND_URL;

// Prompt padrão usado APENAS quando o painel Integrações de IA está vazio. A fonte de
// verdade é o prompt salvo no painel — nenhum sufixo/instrução fixa é acrescentado.
const DEFAULT_PRODUCT_PROMPT =
  'Fotografia editorial premium de tecido para catálogo de alto padrão. Produto: {tecido}. ' +
  'REFERÊNCIA VISUAL: a foto real enviada é a fonte de verdade do tecido — preservar com fidelidade máxima cor, tonalidade, estampa, desenho, textura, brilho e caimento reais. A foto de referência NÃO define o layout: transformar o registro simples/close-up em uma composição editorial nova. ' +
  'COMPOSIÇÃO: tecido real como protagonista, disposto com dobras amplas, suaves e naturais, ocupando aproximadamente 55–65% da composição; fundo neutro claro e sofisticado; espaço negativo claramente reservado na região superior esquerda. ' +
  'TEXTO: escrever na área de espaço negativo SOMENTE o nome exato do produto ("{tecido}"), em tipografia serifada elegante e legível, opcionalmente com uma linha fina e discreta abaixo do nome. Nenhum outro texto. ' +
  'PROIBIDO: nome de variante/cor, composição, características, slogans, aplicações, logotipo, marca-d\'água, lupa, círculo de detalhe, ícones, pessoas, manequins, roupas confeccionadas e elementos promocionais. ' +
  'PRIORIDADES: fidelidade ao tecido real; nome correto do produto; padronização visual entre produtos; apresentação comercial elegante.';

// Gerador da FOTO PRINCIPAL (capa comercial) do produto com IA.
// Usa uma foto REAL de variante como referência visual. NÃO gera fotos de variantes.
// Fluxo: selecionar referência → conferir prompt → gerar prévia → gerar novamente → aprovar e salvar.
export function CoverAIGenerator({ productName, variants, cover, onApprove, onRemove }) {
  const { user } = useQueue();
  const [open, setOpen] = useState(false);
  const [base, setBase] = useState(DEFAULT_PRODUCT_PROMPT);
  const [refId, setRefId] = useState(null);
  // Foto de referência enviada na hora (câmera do celular ou galeria) — alternativa
  // às fotos de variantes. Quando selecionada, refId = '__custom__'.
  const [customRef, setCustomRef] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const withPhoto = (variants || []).filter((v) => v.image);
  const vid = (v) => v.id || v.key;

  // Monta o prompt final: o texto vem do painel (fonte de verdade). O nome exato do
  // produto SEMPRE chega ao modelo — via {tecido} quando presente, ou acrescentado ao
  // final quando o admin removeu o placeholder (sem duplicar nesses casos).
  const buildPrompt = (basePrompt) => {
    const name = (productName || 'tecido').trim();
    let p = basePrompt || DEFAULT_PRODUCT_PROMPT;
    if (p.includes('{tecido}')) {
      p = p.replaceAll('{tecido}', name);
    } else {
      p = `${p}\n\nNome do produto — único texto permitido na imagem, em tipografia serifada elegante na área de espaço negativo: "${name}".`;
    }
    // A capa é do produto-mãe: nome de variante/cor nunca entra na imagem.
    return p.replaceAll('{variante}', 'capa comercial do produto (sem nome de variante/cor na imagem)');
  };

  const openPanel = async () => {
    let basePrompt = DEFAULT_PRODUCT_PROMPT;
    try {
      const res = await fetch(`${API}/api/ai/settings`, { headers: { 'X-Admin-Role': user?.role || '' } });
      if (res.ok) {
        const s = await res.json();
        basePrompt = s.promptProductImage || basePrompt;
      }
    } catch {}
    const ref = withPhoto[0] || null;
    setBase(basePrompt);
    setRefId(ref ? vid(ref) : null);
    setCustomRef(null);
    setPrompt(buildPrompt(basePrompt));
    setPreview(null);
    setError('');
    setOpen(true);
  };

  const onRefPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setError('');
      try {
        // Normaliza a foto do celular antes de qualquer envio (EXIF, formato, peso).
        const dataUrl = await normalizeImageForAI(file);
        setCustomRef(dataUrl);
        setRefId('__custom__');
        setPrompt(buildPrompt(base));
      } catch {
        setError('O formato desta foto não é compatível. Escolha outra foto em JPEG, PNG ou WEBP.');
      }
    }
    e.target.value = '';
  };

  const removeCustomRef = () => {
    setCustomRef(null);
    if (refId === '__custom__') {
      const ref = withPhoto[0] || null;
      setRefId(ref ? vid(ref) : null);
      setPrompt(buildPrompt(base));
    }
  };

  const generate = async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const ref = refId === '__custom__' ? { name: 'foto enviada', image: customRef } : withPhoto.find((v) => vid(v) === refId);
      const res = await fetch(`${API}/api/ai/generate-product-cover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' },
        body: JSON.stringify({ prompt, referenceImage: ref?.image || '' }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Resposta não-JSON = o pedido morreu no proxy/servidor web antes do backend.
        // O status ajuda a diagnosticar (413 corpo grande, 502/504 timeout do proxy).
        const hint =
          res.status === 413
            ? 'A foto ficou grande demais para o servidor. Tente novamente — o sistema otimiza a imagem antes de enviar.'
            : res.status === 502 || res.status === 504
              ? 'O servidor demorou demais para responder (a geração com referência pode levar ~1 min). Tente novamente.'
              : '';
        throw new Error(d.detail || `Falha ao gerar imagem (HTTP ${res.status}). ${hint}`.trim());
      }
      if (!d.image) throw new Error('Falha ao gerar imagem: o servidor respondeu sem imagem.');
      setPreview(d.image);
    } catch (e) {
      setError(/failed to fetch|networkerror/i.test(e.message || '') ? 'Sem conexão com o servidor. Verifique a internet e tente novamente.' : e.message);
    } finally {
      setLoading(false);
    }
  };

  const approve = () => {
    // A prévia já é uma URL estável do armazenamento durável do servidor (gerada e persistida pelo backend).
    onApprove(preview);
    setOpen(false);
    setPreview(null);
  };

  return (
    <div data-testid="cover-ai">
      {cover && !open && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-navy/10 px-2.5 py-0.5 text-[11px] font-semibold text-navy" data-testid="cover-ai-active-badge">
            Capa gerada por IA ativa
          </span>
          {confirmRemove ? (
            <span className="flex items-center gap-2" data-testid="cover-ai-remove-confirm">
              <span className="text-xs font-medium text-red-700">Remover a capa? O produto volta a usar a foto real da primeira variante.</span>
              <button type="button" onClick={() => setConfirmRemove(false)} data-testid="cover-ai-remove-cancel" className="rounded-full border border-navy/20 px-3 py-1.5 text-[11px] font-semibold text-navy">
                CANCELAR
              </button>
              <button
                type="button"
                onClick={() => {
                  onRemove();
                  setConfirmRemove(false);
                }}
                data-testid="cover-ai-remove-do"
                className="rounded-full bg-red-600 px-3 py-1.5 text-[11px] font-semibold text-white"
              >
                REMOVER
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmRemove(true)} data-testid="cover-ai-remove" className="text-[11px] font-semibold text-red-700 underline">
              Remover capa
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        data-testid="cover-ai-open"
        className="flex h-10 items-center justify-center gap-2 rounded-xl border border-dashed border-navy/30 px-4 text-xs font-semibold text-navy transition-colors hover:bg-navy-light"
      >
        <Sparkles className="h-4 w-4" /> {open ? 'FECHAR GERADOR' : 'GERAR FOTO PRINCIPAL COM IA'}
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-navy/15 bg-white p-3" data-testid="cover-ai-panel">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">1. Foto real de referência</p>
          {withPhoto.length === 0 && !customRef && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="cover-ai-no-ref">
              Nenhuma variante com foto real. Envie uma foto do tecido abaixo (câmera ou galeria) — ou cadastre a foto de uma variante.
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="cover-ai-refs">
            {withPhoto.map((v) => (
              <button
                key={vid(v)}
                type="button"
                onClick={() => {
                  setRefId(vid(v));
                  setPrompt(buildPrompt(base));
                }}
                aria-pressed={refId === vid(v)}
                data-testid={`cover-ai-ref-${vid(v)}`}
                className={`overflow-hidden rounded-lg border-2 transition-colors ${refId === vid(v) ? 'border-navy' : 'border-transparent opacity-70 hover:opacity-100'}`}
                title={v.name}
              >
                <img src={v.image} alt={v.name} className="h-16 w-16 object-cover" />
              </button>
            ))}
            {customRef && (
              <span className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setRefId('__custom__');
                    setPrompt(buildPrompt(base));
                  }}
                  aria-pressed={refId === '__custom__'}
                  data-testid="cover-ai-ref-custom"
                  className={`overflow-hidden rounded-lg border-2 transition-colors ${refId === '__custom__' ? 'border-navy' : 'border-transparent opacity-70 hover:opacity-100'}`}
                  title="Foto enviada"
                >
                  <img src={customRef} alt="Foto de referência enviada" className="h-16 w-16 object-cover" />
                </button>
                <button
                  type="button"
                  onClick={removeCustomRef}
                  aria-label="Remover foto enviada"
                  data-testid="cover-ai-ref-custom-remove"
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold text-white"
                >
                  ×
                </button>
              </span>
            )}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => document.getElementById('cover-ref-camera')?.click()}
              data-testid="cover-ai-ref-camera-button"
              className="flex h-10 items-center justify-center gap-2 rounded-xl bg-navy text-[11px] font-bold text-white hover:bg-navy-hover"
            >
              <Camera className="h-4 w-4" /> TIRAR FOTO
            </button>
            <button
              type="button"
              onClick={() => document.getElementById('cover-ref-gallery')?.click()}
              data-testid="cover-ai-ref-gallery-button"
              className="flex h-10 items-center justify-center gap-2 rounded-xl border border-navy/25 text-[11px] font-bold text-navy hover:bg-navy-light"
            >
              ESCOLHER DA GALERIA
            </button>
          </div>
          <input id="cover-ref-camera" data-testid="cover-ai-ref-camera-input" type="file" accept="image/*" capture="environment" className="sr-only" onChange={onRefPhoto} />
          <input id="cover-ref-gallery" data-testid="cover-ai-ref-gallery-input" type="file" accept="image/*" className="sr-only" onChange={onRefPhoto} />
          <p className="mt-1.5 text-[10px] text-muted-foreground">
            Use uma foto real do tecido como referência: a IA mantém cor, estampa e textura fiéis na capa comercial.
          </p>

          <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">2. Prompt — confira antes de gerar</p>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            data-testid="cover-ai-prompt"
            className="mt-1 w-full rounded-lg border border-input bg-white px-3 py-2 text-xs outline-none focus:border-navy"
          />

          {error && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" data-testid="cover-ai-error">{error}</p>
          )}
          {preview && <img src={preview} alt="Prévia da capa gerada por IA" className="mt-3 w-full rounded-lg" data-testid="cover-ai-preview" />}

          <div className="mt-3 flex flex-wrap gap-2">
            {!preview ? (
              <button
                type="button"
                onClick={generate}
                disabled={loading || !refId || (refId === '__custom__' && !customRef)}
                data-testid="cover-ai-generate"
                className="flex h-11 items-center gap-2 rounded-xl bg-navy px-5 text-xs font-bold text-white hover:bg-navy-hover disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" /> {loading ? 'GERANDO…' : 'GERAR PRÉVIA'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={generate}
                  disabled={loading}
                  data-testid="cover-ai-regenerate"
                  className="flex h-11 items-center gap-2 rounded-xl border border-navy/25 px-5 text-xs font-bold text-navy hover:bg-navy-light disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" /> {loading ? 'GERANDO…' : 'GERAR NOVAMENTE'}
                </button>
                <button
                  type="button"
                  onClick={approve}
                  disabled={saving}
                  data-testid="cover-ai-approve"
                  className="flex h-11 items-center gap-2 rounded-xl bg-emerald-600 px-5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" /> {saving ? 'SALVANDO…' : 'APROVAR E SALVAR'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="cover-ai-cancel"
              className="flex h-11 items-center gap-2 rounded-xl border border-navy/20 px-5 text-xs font-semibold text-navy hover:bg-navy-light"
            >
              <X className="h-4 w-4" /> CANCELAR
            </button>
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
            A capa é só comercial: card do catálogo e foto inicial da página do produto. Ao escolher uma variante, o
            cliente sempre vê a foto real dela. Nada entra no ar antes da aprovação.
          </p>
        </div>
      )}
    </div>
  );
}
